import { memo } from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { CrmMessageBatchHistoryItem } from "../types";

export interface CrmMessageBatchHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  batchHistory: CrmMessageBatchHistoryItem[];
}

const CrmMessageBatchHistoryDialog = memo(
  ({ open, onClose, batchHistory }: CrmMessageBatchHistoryDialogProps) => {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="crm-message-history-dialog-title"
      >
        <DialogTitle id="crm-message-history-dialog-title">
          Histórico completo de mensagens enviadas
        </DialogTitle>
        <DialogContent dividers>
          {batchHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum envio encontrado até o momento.
            </Typography>
          ) : (
            <List dense disablePadding>
              {batchHistory.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{
                    px: 0,
                    py: 0.75,
                    alignItems: "flex-start",
                    borderBottom: "1px dashed",
                    borderColor: "divider",
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" color="text.primary">
                          {item.dateLabel}
                        </Typography>
                        <Chip
                          size="small"
                          label={item.statusLabel}
                          color={
                            item.status === "completed"
                              ? "success"
                              : item.status === "inProgress"
                                ? "warning"
                                : item.status === "failed"
                                  ? "error"
                                  : "default"
                          }
                          sx={{ minWidth: 110 }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Solicitadas: {item.requestedCount.toLocaleString("pt-BR")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enviadas: {item.sentCount.toLocaleString("pt-BR")}
                        </Typography>
                        {item.failedCount > 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Não enviadas: {item.failedCount.toLocaleString("pt-BR")}
                          </Typography>
                        ) : null}
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} sx={{ textTransform: "none" }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

CrmMessageBatchHistoryDialog.displayName = "CrmMessageBatchHistoryDialog";

export default CrmMessageBatchHistoryDialog;
